import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/ui/button'
import { useHelp } from './help-provider'
import { getHelpContent } from './registry'
import { computeTourCardPosition, TOUR_CARD_WIDTH } from './walkthrough-position'

/**
 * Renders the active first-run walkthrough as a text overlay: a dim backdrop,
 * an optional highlight ring around the step's target element, and a tip card
 * with Back / Next / Skip / Done. Steps anchor to `data-tour="<target>"`; a
 * step with no target (or whose target is hidden or off-page) shows a centered
 * card instead. Mounted once in AppLayout. No third-party tour dependency.
 */

type Rect = {
  readonly top: number
  readonly left: number
  readonly width: number
  readonly height: number
}

export function WalkthroughHost() {
  const { activeTour, closeTour } = useHelp()
  const content = activeTour ? getHelpContent(activeTour) : null
  const steps = content?.tour ?? []
  const total = steps.length

  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<Rect | null>(null)
  const cardRef = useRef<HTMLDivElement | null>(null)
  const [cardH, setCardH] = useState(0)

  // Restart at the first step each time a tour opens.
  useEffect(() => {
    setIndex(0)
  }, [activeTour])

  const step = index < total ? steps[index] : undefined

  const goNext = useCallback(() => setIndex((i) => Math.min(i + 1, total - 1)), [total])
  const goBack = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), [])

  // Bring the target into view once when the step changes.
  useEffect(() => {
    if (!step?.target) return
    document
      .querySelector(`[data-tour="${step.target}"]`)
      ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [step])

  // Keep the highlight rect in sync with scroll and resize.
  useLayoutEffect(() => {
    if (!activeTour || !step?.target) {
      setRect(null)
      return
    }
    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`)
      if (!el) {
        setRect(null)
        return
      }
      const r = el.getBoundingClientRect()
      // Hidden / display:none targets report a zero box; fall back to centered.
      if (r.width === 0 && r.height === 0) {
        setRect(null)
        return
      }
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [activeTour, step])

  // Esc closes the tour.
  useEffect(() => {
    if (!activeTour) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeTour()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeTour, closeTour])

  // Measure the card each render so it can be kept fully on-screen.
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight ?? 0
    setCardH((prev) => (Math.abs(prev - h) > 1 ? h : prev))
  })

  if (!activeTour || !content || !step) return null

  const isLast = index === total - 1

  // Position the tip card so it is always fully inside the viewport.
  const { top: cardTop, left: cardLeft } = computeTourCardPosition(
    rect,
    cardH,
    window.innerWidth,
    window.innerHeight,
  )
  const cardStyle: CSSProperties = { position: 'fixed', top: cardTop, left: cardLeft }

  return createPortal(
    <div className="fixed inset-0 z-[70]">
      {rect ? (
        <>
          {/* Transparent layer blocks page interaction; the ring's box-shadow dims. */}
          <div className="absolute inset-0" />
          <div
            className="pointer-events-none absolute rounded-lg ring-2 ring-[var(--color-cobalt-bright)] transition-all duration-200"
            style={{
              top: rect.top - 6,
              left: rect.left - 6,
              width: rect.width + 12,
              height: rect.height + 12,
              boxShadow: '0 0 0 9999px rgba(3, 5, 12, 0.72)',
            }}
          />
        </>
      ) : (
        <div className="absolute inset-0 bg-[rgba(3,5,12,0.72)]" />
      )}

      <div
        ref={cardRef}
        style={{
          ...cardStyle,
          width: TOUR_CARD_WIDTH,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'calc(100vh - 24px)',
          overflowY: 'auto',
        }}
        className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[var(--color-surface)] p-4 shadow-[0_16px_48px_rgba(0,0,0,0.5)]"
      >
        <div className="mb-1.5 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-[var(--color-cobalt-bright)]">
          {content.title} · Step {index + 1} of {total}
        </div>
        <h3 className="mb-1.5 text-base font-semibold text-[var(--color-silver-bright)]">
          {step.title}
        </h3>
        <p className="mb-4 text-sm leading-relaxed text-[var(--color-silver)]">{step.body}</p>
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={closeTour}
            className="text-xs text-[var(--color-silver)] transition-colors hover:text-[var(--color-silver-bright)]"
          >
            {isLast ? 'Close' : 'Skip'}
          </button>
          <div className="flex items-center gap-2">
            {index > 0 && (
              <Button variant="outline" size="sm" onClick={goBack}>
                Back
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={closeTour}>
                Done
              </Button>
            ) : (
              <Button size="sm" onClick={goNext}>
                Next
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
